import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { ArrowLeft, Mail, Lock, Eye, EyeOff } from 'lucide-react';

export default function SignInPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!email || !pw) { setError('Please enter your email and password.'); return; }
    setSubmitting(true);
    try {
      await signIn(email.trim().toLowerCase(), pw);
      navigate('/', { replace: true });
    } catch (err) {
      if (err.code === 'RATE_LIMITED') {
        setError(`Too many attempts. Try again in ${err.details?.retry_after_seconds ?? 'a few'}s.`);
      } else if (err.code === 'VERIFICATION_INCOMPLETE') {
        setError("This account isn't fully verified yet. Finish sign-up to continue.");
      } else {
        setError(err.message || 'Sign in failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="sign-in-screen">
      <div className="w-full max-w-md">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> back
        </button>

        <div className="mb-1 flex items-center gap-2">
          <img src="/allviews-mark.svg" alt="" className="h-5 w-5" />
          <img src="/allviews-wordmark.svg" alt="ALLVIEWS" className="h-3.5" />
          <span className="text-xs text-[#5B6472] ml-1">Sign in</span>
        </div>

        <h1 className="text-2xl font-extrabold text-[#0B1B46] mt-6 mb-1" data-testid="screen-title">Sign In</h1>
        <p className="text-sm text-[#5B6472] mb-6">Welcome back. Use the email and password you registered with.</p>

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="sign-in-email" className="block text-sm font-semibold text-[#0B1B46] mb-1.5">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
              <input
                id="sign-in-email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 pl-10 pr-4 border border-[#E3E8EF] rounded-lg text-sm text-[#0B1B46] placeholder:text-[#98A0AD] focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:border-transparent bg-white"
                data-testid="sign-in-email"
              />
            </div>
          </div>
          <div>
            <label htmlFor="sign-in-password" className="block text-sm font-semibold text-[#0B1B46] mb-1.5">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
              <input
                id="sign-in-password"
                type={showPw ? 'text' : 'password'}
                placeholder="Your password"
                autoComplete="current-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="w-full h-11 pl-10 pr-10 border border-[#E3E8EF] rounded-lg text-sm text-[#0B1B46] placeholder:text-[#98A0AD] focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:border-transparent bg-white"
                data-testid="sign-in-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B6472] hover:text-[#0B1B46]"
                tabIndex={-1}
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]" data-testid="sign-in-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="h-11 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
            data-testid="sign-in-submit-btn"
          >
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <Link to="/register" className="text-[#146BFF] font-semibold text-sm hover:underline" data-testid="sign-in-to-register-btn">
            Create an account instead
          </Link>
        </div>

        {/* Dev credentials hint */}
        <div className="mt-6 bg-[#F3F5F8] border border-[#E3E8EF] rounded-xl p-4" data-testid="sign-in-dev-hint">
          <p className="text-[10px] font-extrabold text-[#5B6472] tracking-widest uppercase mb-2">Dev / Test accounts</p>
          <div className="flex flex-col gap-2">
            <button type="button"
              onClick={() => { setEmail('emma@example.com'); setPw('Passw0rd!'); }}
              className="flex items-center justify-between w-full px-3 py-2 bg-white border border-[#E3E8EF] rounded-lg text-left hover:bg-[#EFF5FF] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
              data-testid="sign-in-dev-contributor">
              <div>
                <p className="text-sm font-bold text-[#0B1B46]">Contributor</p>
                <p className="text-xs text-[#5B6472]">emma@example.com</p>
              </div>
              <span className="px-2 py-0.5 text-[10px] font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">Fill</span>
            </button>
            <button type="button"
              onClick={() => { setEmail('sarah@example.com'); setPw('Passw0rd!'); }}
              className="flex items-center justify-between w-full px-3 py-2 bg-white border border-[#E3E8EF] rounded-lg text-left hover:bg-[#EFF5FF] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
              data-testid="sign-in-dev-host">
              <div>
                <p className="text-sm font-bold text-[#0B1B46]">Host</p>
                <p className="text-xs text-[#5B6472]">sarah@example.com</p>
              </div>
              <span className="px-2 py-0.5 text-[10px] font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">Fill</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
